import { useMoveFlow } from '@ofself/shell'
import { useChat } from '../contexts/ChatContext'
import { ALL, UNSORTED, inWorld, worldName, worldRooms } from '../utils/worlds'

/* THE THREE VERBS OF A TAG (owner call 2026-10-04), every door: MOVE (out of
   the world you're in, into another: the selection bar, a row's right-click,
   ⇧⌘M, a drop on a world in the drawer or the atrium), ADD TO (into another
   world as well, out of none) and REMOVE FROM (out of the world you're in;
   they stay in All channels). Move and Add are each the shell's useMoveFlow
   (the picker, the rows that fold, "Added 3 channels to Product" with Undo,
   the unfold on a failure); Remove is the move flow's `remove` ("Removed 2
   channels from Product"), the same write toward All channels.
   What each MEANS is utils/worlds.js's (planMove); the write and its undo are
   ChatContext's (moveChannels).

   `sel` is the column's useListSelection (absent on the atrium). */
export default function useWorldMove(sel) {
  const { activeGroup, activeWorld, worldsByGroup, groupChannelsByGroup, moveChannels, createWorld, deleteWorld } = useChat()
  const gid = activeGroup?.id
  const worlds = (gid && worldsByGroup[gid]) || []
  const channels = (gid && groupChannelsByGroup[gid]) || []

  // every room a channel can move into (All channels is no place: it is every channel)
  const rooms = worldRooms(worlds).map((id) => ({
    id, name: worldName(id, worlds), count: channels.filter((c) => inWorld(c, id, worlds)).length,
  }))

  /* THE WRITE: into a world that stands, or one founded by its name in the same
     step (its undo retires it again once every channel it took is back).
     Throws when nothing moved, so the picker keeps itself open with the reason. */
  const write = async (ids, to, keep, founded = null) => {
    const r = await moveChannels(gid, ids.filter((id) => channels.some((c) => c.id === id)), { to, from: activeWorld, keep })
    if (!r.moved.length && !r.unchanged.length) throw new Error(r.failed[0]?.error || 'these channels cannot move')
    const undo = async () => {
      const u = await r.undo()
      if (u.failed.length) throw new Error(`${u.failed.length} could not be put back: ${u.failed[0].error}`)
      if (founded) deleteWorld(gid, founded.id)
    }
    return { undo: r.moved.length ? undo : null, to: r.to, failed: r.failed }
  }
  const into = (keep) => async (ids, target) => {
    if (target.create == null) return write(ids, target.to.id, keep)
    const founded = await createWorld(gid, target.create)
    if (!founded) throw new Error(`could not make “${target.create}”`)
    return write(ids, founded.id, keep, founded)
  }

  const currentId = activeWorld === ALL ? null : activeWorld
  // Remove from the world you're in (none in All channels or Unsorted: they are no world to leave):
  // out of it toward All channels, the toast naming the world they left
  const world = worlds.find((w) => w.id === activeWorld)
  const remove = world && (async (ids) => ({ ...(await write(ids, ALL, false)), to: undefined }))
  // from All channels nothing leaves the list in force; from a world (or Unsorted) the moved rows do
  const flow = useMoveFlow({ move: into(false), remove, rooms, selection: sel, noun: 'channel', kind: 'world', currentId })
  // an Add joins a world (Unsorted is "in none", so no place to join) and nothing leaves the list
  const add = useMoveFlow({ move: into(true), rooms: rooms.filter((r) => r.id !== UNSORTED), selection: sel, noun: 'channel',
    kind: 'world', verb: 'Add', currentId, leaving: () => [] })

  return { ...flow, rooms, add, world }
}
